import React, { useState, useEffect, useCallback } from 'react';
import {
  Space, Typography, Button, Image, Pagination, App, Breadcrumb, Spin, Empty,
  Upload, Tooltip, Modal, InputNumber, Form, Progress, Slider,
} from 'antd';
import {
  CopyOutlined, InboxOutlined, ReloadOutlined, UploadOutlined,
  ZoomInOutlined, ZoomOutOutlined,
} from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import type { RcFile } from 'antd/es/upload';
import { useAuth } from '../context/AuthContext';
import { imagesApi, ImageItem } from '../api/images';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const PAGE_SIZE = 24;

const LS_WIDTH  = 'img-resize-width';
const LS_HEIGHT = 'img-resize-height';

// ── Canvas crop + WebP conversion ─────────────────────────────────────────────

async function cropAndConvertToWebP(
  file: File,
  croppedAreaPixels: Area,
  outputWidth: number,
  outputHeight: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(
        img,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        outputWidth,
        outputHeight,
      );
      canvas.toBlob(
        blob => {
          if (!blob) { reject(new Error('Canvas conversion failed')); return; }
          const baseName = file.name.replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${baseName}.webp`, { type: 'image/webp' }));
        },
        'image/webp',
        0.92,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
    img.src = objectUrl;
  });
}

// ── Per-file crop modal ───────────────────────────────────────────────────────

interface CropModalProps {
  file: RcFile;
  fileIndex: number;
  fileTotal: number;
  outputWidth: number;
  outputHeight: number;
  onWidthChange: (v: number) => void;
  onHeightChange: (v: number) => void;
  onConfirm: (croppedFile: File) => void;
  onSkip: () => void;
  onCancel: () => void;
  uploading: boolean;
}

const CropModal: React.FC<CropModalProps> = ({
  file, fileIndex, fileTotal,
  outputWidth, outputHeight, onWidthChange, onHeightChange,
  onConfirm, onSkip, onCancel, uploading,
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop,   setCrop]   = useState({ x: 0, y: 0 });
  const [zoom,   setZoom]   = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    // reset position when file changes
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const aspect = outputWidth / outputHeight;

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const result = await cropAndConvertToWebP(file, croppedAreaPixels, outputWidth, outputHeight);
      onConfirm(result);
    } finally {
      setProcessing(false);
    }
  };

  const title = fileTotal > 1
    ? `Crop & Upload — ${fileIndex + 1} of ${fileTotal}: ${file.name}`
    : `Crop & Upload — ${file.name}`;

  return (
    <Modal
      title={title}
      open
      onCancel={processing || uploading ? undefined : onCancel}
      closable={!processing && !uploading}
      maskClosable={false}
      footer={null}
      width={600}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Cropper canvas */}
        <div style={{ position: 'relative', width: '100%', height: 340, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          )}
        </div>

        {/* Zoom slider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Zoom</Text>
          <Slider
            min={1} max={3} step={0.01}
            value={zoom}
            onChange={setZoom}
            style={{ flex: 1 }}
          />
        </div>

        {/* Output dimensions */}
        <Form layout="inline">
          <Form.Item label="Output width">
            <InputNumber
              min={1} max={8000} value={outputWidth}
              onChange={v => v && onWidthChange(v)}
              addonAfter="px" style={{ width: 145 }}
            />
          </Form.Item>
          <Form.Item label="Height">
            <InputNumber
              min={1} max={8000} value={outputHeight}
              onChange={v => v && onHeightChange(v)}
              addonAfter="px" style={{ width: 145 }}
            />
          </Form.Item>
        </Form>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Output will be converted to WebP (quality 92%).
        </Text>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel} disabled={processing || uploading}>Cancel all</Button>
          {fileTotal > 1 && (
            <Button onClick={onSkip} disabled={processing || uploading}>Skip</Button>
          )}
          <Button
            type="primary"
            icon={<UploadOutlined />}
            loading={processing || uploading}
            onClick={handleConfirm}
            disabled={!croppedAreaPixels}
          >
            {processing || uploading ? 'Uploading…' : (fileIndex + 1 < fileTotal ? 'Crop & next' : 'Crop & upload')}
          </Button>
        </div>
      </Space>
    </Modal>
  );
};

// ── Upload queue manager ──────────────────────────────────────────────────────

interface UploadQueueProps {
  files: RcFile[];
  onDone: () => void;
  onCancel: () => void;
  uploadFn: (file: File, onProgress: (p: number) => void) => Promise<void>;
}

const UploadQueue: React.FC<UploadQueueProps> = ({ files, onDone, onCancel, uploadFn }) => {
  const { message } = App.useApp();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [outputWidth,  setOutputWidth]  = useState<number>(() => parseInt(localStorage.getItem(LS_WIDTH)  || '1280'));
  const [outputHeight, setOutputHeight] = useState<number>(() => parseInt(localStorage.getItem(LS_HEIGHT) || '720'));

  const handleWidthChange  = (v: number) => { setOutputWidth(v);  localStorage.setItem(LS_WIDTH,  String(v)); };
  const handleHeightChange = (v: number) => { setOutputHeight(v); localStorage.setItem(LS_HEIGHT, String(v)); };

  const handleConfirm = async (croppedFile: File) => {
    setUploading(true);
    setProgress(0);
    try {
      await uploadFn(croppedFile, p => setProgress(p));
    } catch {
      message.error(`Upload failed: ${files[currentIndex].name}`);
    } finally {
      setUploading(false);
      setProgress(0);
    }

    if (currentIndex + 1 >= files.length) {
      onDone();
    } else {
      setCurrentIndex(i => i + 1);
    }
  };

  const handleSkip = () => {
    if (currentIndex + 1 >= files.length) {
      onDone();
    } else {
      setCurrentIndex(i => i + 1);
    }
  };

  if (currentIndex >= files.length) return null;

  return (
    <>
      <CropModal
        file={files[currentIndex]}
        fileIndex={currentIndex}
        fileTotal={files.length}
        outputWidth={outputWidth}
        outputHeight={outputHeight}
        onWidthChange={handleWidthChange}
        onHeightChange={handleHeightChange}
        onConfirm={handleConfirm}
        onSkip={handleSkip}
        onCancel={onCancel}
        uploading={uploading}
      />
      {uploading && (
        <Modal open footer={null} closable={false} width={320} centered>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Uploading {files[currentIndex].name}…</Text>
            <Progress percent={progress} status="active" />
          </Space>
        </Modal>
      )}
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ImagesPage: React.FC = () => {
  const { token } = useAuth();
  const { message } = App.useApp();
  const api = useCallback(() => imagesApi(token!), [token]);

  const [images,  setImages]  = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);

  const [pendingFiles,  setPendingFiles]  = useState<RcFile[]>([]);
  const [queueActive,   setQueueActive]   = useState(false);

  const fetchImages = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await api().getList(p, PAGE_SIZE);
      setImages(res.images);
      setTotal(res.pagination.total);
    } catch {
      message.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [api, page, message]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleBeforeUpload = useCallback((file: RcFile, fileList: RcFile[]) => {
    if (fileList[0] === file) {
      setPendingFiles(fileList);
      setQueueActive(true);
    }
    return false;
  }, []);

  const doUpload = useCallback(async (file: File, onProgress: (p: number) => void) => {
    await api().upload(file, onProgress);
  }, [api]);

  const handleQueueDone = () => {
    setQueueActive(false);
    setPendingFiles([]);
    setPage(1);
    fetchImages(1);
  };

  const handleQueueCancel = () => {
    setQueueActive(false);
    setPendingFiles([]);
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success('URL copied');
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    fetchImages(p);
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Breadcrumb items={[{ title: 'Images' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>
          Images
          {total > 0 && (
            <Text type="secondary" style={{ fontSize: 14, fontWeight: 400, marginLeft: 12 }}>
              {total} total
            </Text>
          )}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={() => fetchImages()}>Refresh</Button>
      </div>

      <Dragger
        beforeUpload={handleBeforeUpload}
        showUploadList={false}
        accept="image/*"
        multiple
        style={{ padding: '8px 0' }}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Click or drag images here to upload</p>
        <p className="ant-upload-hint">Supports JPG, PNG, GIF, WebP, SVG — crop and convert to WebP before upload</p>
      </Dragger>

      {queueActive && (
        <UploadQueue
          files={pendingFiles}
          onDone={handleQueueDone}
          onCancel={handleQueueCancel}
          uploadFn={doUpload}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      ) : images.length === 0 ? (
        <Empty description="No images yet" style={{ padding: '60px 0' }} />
      ) : (
        <Image.PreviewGroup
          preview={{
            toolbarRender: (_, { actions: { onZoomIn, onZoomOut } }) => (
              <Space size={12} style={{ padding: '8px 16px', background: 'rgba(0,0,0,0.5)', borderRadius: 8 }}>
                <ZoomOutOutlined style={{ color: '#fff', fontSize: 18, cursor: 'pointer' }} onClick={onZoomOut} />
                <ZoomInOutlined  style={{ color: '#fff', fontSize: 18, cursor: 'pointer' }} onClick={onZoomIn} />
              </Space>
            ),
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {images.map(img => (
              <ImageCard key={img.filename} img={img} onCopy={copyUrl} />
            ))}
          </div>
        </Image.PreviewGroup>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={page}
            total={total}
            pageSize={PAGE_SIZE}
            onChange={handlePageChange}
            showTotal={t => `${t} images`}
          />
        </div>
      )}
    </Space>
  );
};

// ── Image card ────────────────────────────────────────────────────────────────

const ImageCard: React.FC<{ img: ImageItem; onCopy: (url: string) => void }> = ({ img, onCopy }) => {
  const [hovered, setHovered] = useState(false);
  const uploadedDate = img.uploadedAt
    ? new Date(img.uploadedAt * 1000).toLocaleDateString()
    : '—';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#f5f5f5' }}
    >
      <Image
        src={img.url}
        alt={img.filename}
        style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
        preview={{ mask: 'Preview' }}
      />
      {hovered && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.65)', padding: '6px 8px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Text style={{ color: '#fff', fontSize: 11, flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {uploadedDate}
          </Text>
          <Tooltip title="Copy URL">
            <Button
              size="small" type="text"
              icon={<CopyOutlined style={{ color: '#fff' }} />}
              onClick={() => onCopy(img.url)}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default ImagesPage;
